// Follows the paged squad sync (`POST /v1/squads/sync?limit&offset`) to the
// end instead of reporting success after the first page. Pure: the caller
// supplies the page fetcher, so the loop can be exercised without a browser.

export type SyncWriteFailure = { run_id: string; stage_id: string; team_id: string; error: string }

export type SyncPage = {
  recorded: number
  unchanged: number
  runs_scanned: number
  runs_total: number
  offset: number
  next_offset: number | null
  complete: boolean
  write_failures?: SyncWriteFailure[]
  write_failure_count?: number
}

export type SyncStop = 'complete' | 'write_failures' | 'max_pages' | 'cancelled' | 'stalled'

export type SyncSummary = {
  pages: number
  recorded: number
  unchanged: number
  runs_scanned: number
  runs_total: number
  write_failure_count: number
  write_failures: SyncWriteFailure[]
  /** True only when every run was visited and nothing failed to write. */
  complete: boolean
  stopped: SyncStop
  /** Offset to resume from when the walk stopped early (page cap or cancel); null otherwise. */
  next_offset: number | null
  /** Offsets of pages that reported write failures; retry them with syncPages(). */
  failed_pages: number[]
  /** Offsets of pages that completed with no write failures (progress to persist). */
  repaired_pages: number[]
}

export type SyncOptions = {
  pageSize?: number
  /** Upper bound on pages per click so a runaway server can never spin the UI forever. */
  maxPages?: number
  signal?: AbortSignal
  onProgress?: (summary: SyncSummary) => void
  /** Resume cursor from a previous summary's next_offset. */
  startOffset?: number
}

export const SYNC_PAGE_SIZE = 200
export const SYNC_MAX_PAGES = 50
const MAX_FAILURES_KEPT = 50

export async function syncAllRuns(
  fetchPage: (offset: number, limit: number) => Promise<SyncPage>,
  options: SyncOptions = {},
): Promise<SyncSummary> {
  const pageSize = options.pageSize ?? SYNC_PAGE_SIZE
  const maxPages = options.maxPages ?? SYNC_MAX_PAGES
  const summary: SyncSummary = {
    pages: 0,
    recorded: 0,
    unchanged: 0,
    runs_scanned: 0,
    runs_total: 0,
    write_failure_count: 0,
    write_failures: [],
    complete: false,
    stopped: 'max_pages',
    next_offset: null,
    failed_pages: [],
    repaired_pages: [],
  }
  let offset = Math.max(0, Math.floor(options.startOffset ?? 0))
  while (summary.pages < maxPages) {
    if (options.signal?.aborted) {
      // Resume from the page that was not fetched.
      summary.stopped = 'cancelled'
      summary.next_offset = offset
      return summary
    }
    let page: SyncPage
    try {
      page = await fetchPage(offset, pageSize)
    } catch (err) {
      if (options.signal?.aborted) {
        // The in-flight request was aborted: this page did not complete, so
        // the cursor stays on it rather than on where the walk began.
        summary.stopped = 'cancelled'
        summary.next_offset = offset
        return summary
      }
      throw err
    }
    summary.pages += 1
    if ((page.write_failure_count ?? (page.write_failures || []).length) > 0) summary.failed_pages.push(offset)
    else summary.repaired_pages.push(offset)
    summary.recorded += page.recorded || 0
    summary.unchanged += page.unchanged || 0
    summary.runs_scanned += page.runs_scanned || 0
    summary.runs_total = page.runs_total || summary.runs_total
    summary.write_failure_count += page.write_failure_count ?? (page.write_failures || []).length
    for (const failure of page.write_failures || []) {
      if (summary.write_failures.length < MAX_FAILURES_KEPT) summary.write_failures.push(failure)
    }
    options.onProgress?.({ ...summary, write_failures: [...summary.write_failures], failed_pages: [...summary.failed_pages], repaired_pages: [...summary.repaired_pages] })
    if (page.next_offset === null || page.next_offset === undefined) {
      summary.complete = summary.write_failure_count === 0
      summary.stopped = summary.complete ? 'complete' : 'write_failures'
      summary.next_offset = null
      return summary
    }
    if (page.next_offset <= offset) {
      // A server that does not advance would loop forever; stop and say so.
      summary.stopped = 'stalled'
      summary.next_offset = null
      return summary
    }
    offset = page.next_offset
  }
  // Page cap reached before the last page: keep the cursor so the next click
  // continues from here instead of re-walking from offset zero.
  summary.stopped = 'max_pages'
  summary.next_offset = offset
  return summary
}

/**
 * Re-sync specific page offsets (idempotent on the server: already-recorded
 * stages answer "unchanged"). Returns the offsets that still failed.
 */
export async function syncPages(
  fetchPage: (offset: number, limit: number) => Promise<SyncPage>,
  offsets: number[],
  options: Pick<SyncOptions, 'pageSize' | 'signal' | 'onProgress'> = {},
): Promise<SyncSummary> {
  const pageSize = options.pageSize ?? SYNC_PAGE_SIZE
  const summary: SyncSummary = {
    pages: 0,
    recorded: 0,
    unchanged: 0,
    runs_scanned: 0,
    runs_total: 0,
    write_failure_count: 0,
    write_failures: [],
    complete: false,
    stopped: 'complete',
    next_offset: null,
    failed_pages: [],
    repaired_pages: [],
  }
  for (const offset of Array.from(new Set(offsets)).sort((a, b) => a - b)) {
    if (options.signal?.aborted) {
      summary.stopped = 'cancelled'
      summary.failed_pages.push(offset)
      continue
    }
    let page: SyncPage
    try {
      page = await fetchPage(offset, pageSize)
    } catch (err) {
      if (options.signal?.aborted) {
        summary.stopped = 'cancelled'
        summary.failed_pages.push(offset)
        continue
      }
      throw err
    }
    summary.pages += 1
    summary.recorded += page.recorded || 0
    summary.unchanged += page.unchanged || 0
    summary.runs_scanned += page.runs_scanned || 0
    summary.runs_total = page.runs_total || summary.runs_total
    const failures = page.write_failure_count ?? (page.write_failures || []).length
    summary.write_failure_count += failures
    for (const failure of page.write_failures || []) {
      if (summary.write_failures.length < MAX_FAILURES_KEPT) summary.write_failures.push(failure)
    }
    if (failures > 0) summary.failed_pages.push(offset)
    else summary.repaired_pages.push(offset)
    options.onProgress?.({ ...summary, write_failures: [...summary.write_failures], failed_pages: [...summary.failed_pages], repaired_pages: [...summary.repaired_pages] })
  }
  summary.complete = summary.failed_pages.length === 0 && summary.stopped !== 'cancelled'
  if (!summary.complete && summary.stopped !== 'cancelled') summary.stopped = 'write_failures'
  return summary
}

export function describeSync(summary: SyncSummary): string {
  const base = `${summary.runs_scanned} of ${summary.runs_total} runs over ${summary.pages} page(s): ${summary.recorded} recorded, ${summary.unchanged} unchanged`
  switch (summary.stopped) {
    case 'complete':
      return `Activity synced from SDLC runs (${base})`
    case 'write_failures':
      return `Sync visited every run but ${summary.write_failure_count} stage write(s) failed on ${summary.failed_pages.length} page(s); use "Retry failed pages" (${base})`
    case 'max_pages':
      return `Sync paused after ${summary.pages} pages at run ${summary.next_offset} of ${summary.runs_total}; click "Continue sync" to resume from there (${base})`
    case 'cancelled':
      return `Sync cancelled at run ${summary.next_offset}; click "Continue sync" to resume from there (${base})`
    case 'stalled':
      return `Sync stopped: the server did not advance past offset ${summary.runs_scanned} (${base})`
  }
}
